
import { Ref, ref } from "vue";
import { Component, Vue, Prop } from "vue-property-decorator";

import admission_routes from "@/api/routes/admission";

import CInfiniteScroll from "@/components/InfiniteScroll.vue";

import { AdmissionRole } from "@/models/admission/role";
import { IAdmissionUserRole } from "@/models/admission/user_role";

interface IAdmissionUserRoleOption extends IAdmissionUserRole {
  is_check: boolean;
}

@Component({
  name: "CAdmissionRoleChangeUserRoles",
  components: {
    CInfiniteScroll
  }
})
export default class CAdmissionRoleChangeUserRoles extends Vue {
  @Prop({ required: true }) public role!: AdmissionRole;

  public user_roles_key: number = 0; // для ререндера
  public user_roles_key_available: number = 0; // для ререндера
  public user_roles_key_exists: number = 0; // для ререндера
  public search: string = "";

  public show_filter: boolean = false;
  public filters_count: number = 0;
  public filters: Map<string, boolean> = new Map();

  private available_preload_infinite: boolean = false;
  private available_per_page: number = 100;
  private available_current_page: number = 0;
  private available_next_page: boolean = false;
  public available_user_roles: Ref<Map<string, IAdmissionUserRoleOption>> = ref(
    new Map()
  );
  public available_user_roles_key: number = 0; // для ререндера
  public available_user_roles_select_all: boolean = false;
  public available_user_roles_checked_count: number = 0;

  private exists_preload_infinite: boolean = false;
  private exists_per_page: number = 100;
  private exists_current_page: number = 0;
  private exists_next_page: boolean = false;
  public exists_user_roles: Ref<Map<string, IAdmissionUserRoleOption>> = ref(
    new Map()
  );
  public exists_user_roles_key: number = 0; // для ререндера
  public exists_user_roles_select_all: boolean = false;
  public exists_user_roles_checked_count: number = 0;

  private ids_to_add: Ref<Set<string>> = ref(new Set<string>());
  private ids_to_del: Ref<Set<string>> = ref(new Set<string>());

  protected async created() {
    this.available_user_roles.value = new Map();
    this.exists_user_roles.value = new Map();

    this.ids_to_add.value = new Set();
    this.ids_to_del.value = new Set();

    const promises = [this.loadAvailableUserRoles()];

    if (this.role.id) {
      promises.push(this.loadExistsUserRoles());
    }

    await Promise.all(promises);

    this.user_roles_key++;
  }

  public async searchUserRoles() {
    await this.apply();
  }

  private async apply() {
    this.available_current_page = 0;
    this.available_next_page = false;
    this.available_user_roles_select_all = false;
    this.available_user_roles.value = new Map();

    await this.loadAvailableUserRoles();

    this.user_roles_key_available++;
  }

  public async availableInfiniteHandler() {
    if (this.available_next_page && !this.available_preload_infinite) {
      await this.loadAvailableUserRoles(this.available_current_page + 1);
    }
  }

  public async existsInfiniteHandler() {
    if (this.exists_next_page && !this.exists_preload_infinite) {
      await this.loadExistsUserRoles(this.exists_current_page + 1);
    }
  }

  private async loadAvailableUserRoles(page: number = 0) {
    this.available_preload_infinite = true;

    return this.$api
      .get(admission_routes.user_roles_available(this.role.id), {
        params: {
          page,
          per_page: this.available_per_page,
          search: this.search
        }
      })
      .then(({ data: res }: { data: IAdmissionUserRole[] }) => {
        this.available_current_page = page;
        this.available_next_page = res.length === this.available_per_page;

        res.forEach(user_role => {
          if (!this.exists_user_roles.value.has(user_role.id)) {
            this.available_user_roles.value.set(user_role.id, {
              ...user_role,
              is_check: this.available_user_roles_select_all
            });
          }
        });
      })
      .finally(() => {
        this.available_preload_infinite = false;
        this.available_user_roles_key++;
      });
  }

  private async loadExistsUserRoles(page: number = 0) {
    this.exists_preload_infinite = true;

    return this.$api
      .get(admission_routes.user_roles_exists(this.role.id), {
        params: {
          page,
          per_page: this.exists_per_page
        }
      })
      .then(({ data: res }: { data: IAdmissionUserRole[] }) => {
        this.exists_current_page = page;
        this.exists_next_page = res.length === this.exists_per_page;

        res.forEach(user_role => {
          this.exists_user_roles.value.set(user_role.id, {
            ...user_role,
            is_check: this.exists_user_roles_select_all
          });
        });
      })
      .finally(() => {
        this.exists_preload_infinite = false;
        this.exists_user_roles_key++;
      });
  }

  public checkAllAvailableUserRoles() {
    if (this.available_user_roles_select_all) {
      this.available_user_roles.value.forEach(user_role => {
        user_role.is_check = false;
      });

      this.available_user_roles_checked_count = 0;
      this.available_user_roles_select_all = false;
    } else {
      this.available_user_roles.value.forEach(user_role => {
        user_role.is_check = true;
      });

      this.available_user_roles_checked_count = this.available_user_roles.value.size;
      this.available_user_roles_select_all = true;
    }
  }

  public checkAllIconAvailableUserRoles() {
    return this.available_user_roles_checked_count === 0
      ? null
      : this.available_user_roles_select_all
      ? "/img/icon-plus.svg"
      : "/img/icon-minus.svg";
  }

  public async addAllCheckedAvailableUserRoles() {
    this.available_user_roles.value.forEach(user_role => {
      if (user_role.is_check) {
        user_role.is_check = false;
        this.available_user_roles.value.delete(user_role.id);
        this.exists_user_roles.value.set(user_role.id, user_role);

        this.ids_to_del.value.delete(user_role.id);
        this.ids_to_add.value.add(user_role.id);
      }
    });

    this.available_user_roles_checked_count = 0;
    this.available_user_roles_select_all = false;
    this.exists_user_roles_select_all = false;

    if (!this.available_user_roles.value.size && this.available_next_page) {
      await this.loadAvailableUserRoles(this.available_current_page + 1);

      this.user_roles_key_available++;
    }

    this.$emit("change", {
      add: this.ids_to_add.value,
      del: this.ids_to_del.value
    });
  }

  public checkAvailableUserRole(user_role: IAdmissionUserRoleOption) {
    this.$set(user_role, "is_check", !user_role.is_check);

    this.available_user_roles_checked_count += user_role.is_check ? 1 : -1;
    this.available_user_roles_select_all =
      this.available_user_roles_checked_count ===
      this.available_user_roles.value.size;
  }

  public checkAllExistsUserRoles() {
    if (this.exists_user_roles_select_all) {
      this.exists_user_roles.value.forEach(user_role => {
        user_role.is_check = false;
      });

      this.exists_user_roles_checked_count = 0;
      this.exists_user_roles_select_all = false;
    } else {
      this.exists_user_roles.value.forEach(user_role => {
        user_role.is_check = true;
      });

      this.exists_user_roles_checked_count = this.exists_user_roles.value.size;
      this.exists_user_roles_select_all = true;
    }
  }

  public checkAllIconExistsUserRoles() {
    return this.exists_user_roles_checked_count === 0
      ? null
      : this.exists_user_roles_select_all
      ? "/img/icon-plus.svg"
      : "/img/icon-minus.svg";
  }

  public async removeAllCheckedExistsUserRoles() {
    this.exists_user_roles.value.forEach(user_role => {
      if (user_role.is_check) {
        user_role.is_check = false;
        this.exists_user_roles.value.delete(user_role.id);
        this.available_user_roles.value.set(user_role.id, user_role);

        this.ids_to_add.value.delete(user_role.id);
        this.ids_to_del.value.add(user_role.id);
      }
    });

    this.exists_user_roles_checked_count = 0;
    this.available_user_roles_select_all = false;
    this.exists_user_roles_select_all = false;

    if (!this.exists_user_roles.value.size && this.exists_next_page) {
      await this.loadExistsUserRoles(this.exists_current_page + 1);

      this.user_roles_key_exists++;
    }

    this.$emit("change", {
      add: this.ids_to_add.value,
      del: this.ids_to_del.value
    });
  }

  public checkExistsUserRole(user_role: IAdmissionUserRoleOption) {
    this.$set(user_role, "is_check", !user_role.is_check);

    this.exists_user_roles_checked_count += user_role.is_check ? 1 : -1;
    this.exists_user_roles_select_all =
      this.exists_user_roles_checked_count ===
      this.exists_user_roles.value.size;
  }
}
